'use strict';
import { locale } from "./i18n.js";
import { getLanguageFromLocale } from "./Utils.js";
import {
  f7
} from "framework7-svelte";
import { mapsLoaded, mapsLoading } from "./stores.js";
import { getJSONWithExpiry, setJSONWithExpiry, MONTH_IN_MILLIS } from "./ExpiryStorage.js";

const URL =
  "https://maps.googleapis.com/maps/api/js?key=" +
  process.env.GOOGLE_API_KEY +
  "&libraries=places,geometry&callback=initMap";

let LOCATION_TYPE_SORTING = ["ROOFTOP", "GEOMETRIC_CENTER", "APPROXIMATE", "RANGE_INTERPOLATED"];

let mapsLoadedValue;
let mapsLoadingValue;

let currentLocale;
locale.subscribe(value => {
  currentLocale = value;
});

mapsLoaded.subscribe(value => {
  mapsLoadedValue = value;
});

mapsLoading.subscribe(value => {
  mapsLoadingValue = value;
});

export function initMapService(callback) {
  if (mapsLoadedValue) {
    callback();
  } else if (!mapsLoadingValue) {
    window.initMap = function () {
      mapsLoaded.set(true);
      delete window.initMap;
      callback();
    };

    mapsLoading.set(true);
    const script = document.createElement("script");
    script.src = URL + "&language=" + getLanguageFromLocale(currentLocale);
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }
}

var placesService;
export function getPlacesService() {
  if (!placesService) {
    placesService = new google.maps.places.PlacesService(document.createElement('div'));
  }
  return placesService;
}

var geocoder;
export function getGeocoder() {
  if (!geocoder) {
    geocoder = new google.maps.Geocoder();
  }
  return geocoder;
}

export function geocode(address) {
  let language = getLanguageFromLocale(currentLocale);
  let key = address + "_" + language;
  let result = getJSONWithExpiry(key);
  if (result) {
    return Promise.resolve(result);
  } else {
    return new Promise(function (resolve, reject) {
      initMapService(function () {
        getGeocoder().geocode({
          'address': address,
          language: language
        }, function (results, status) {
          if (status == 'OK') {
            // sort by location type
            results = results.sort(function (a, b) {
              return LOCATION_TYPE_SORTING.indexOf(a.geometry.location_type) - LOCATION_TYPE_SORTING.indexOf(b.geometry.location_type);
            });

            let locations = [];
            results.forEach(pos => {
              let location = pos.geometry.location;
              locations.push({ lat: location.lat(), lng: location.lng() });
            });
            setJSONWithExpiry(key, locations, MONTH_IN_MILLIS);
            resolve(locations);
          } else {
            reject();
          }
        });
      });
    });
  }
}

export function reverseGeocode(location) {
  let language = getLanguageFromLocale(currentLocale);
  let key = location.lat + "," + location.lng + "_" + language;
  let result = getJSONWithExpiry(key);
  if (result) {
    return Promise.resolve(result);
  } else {
    return new Promise(function (resolve, reject) {
      initMapService(function () {
        getGeocoder().geocode({
          'location': location,
          language: language
        }, function (results, status) {
          if (status == 'OK') {
            let result = results.length > 0 ? results[0] : null;
            setJSONWithExpiry(key, result, MONTH_IN_MILLIS);
            resolve(result);
          } else {
            reject();
          }
        });
      });
    });
  }
}

var autocompleteTimer;
export function initAutoComplete(options, callback) {
  let defaultOptions =
  {
    openIn: "dropdown",
    inputEl: '.page-current #searchbar-autocomplete input[type="search"]',
    preloader: true,
    limit: 20,
    valueProperty: "text",
    textProperty: "text",
    source(query, render) {
      clearTimeout(autocompleteTimer);

      let loadItems = function (autocomplete, query, render) {
        autocompleteTimer = setTimeout(function () {
          const results = [];
          if (query.length === 0) {
            render(results);
            return;
          }

          autocomplete.preloaderShow();

          let location = new google.maps.LatLng(8.053529, 98.916525);
          let language = getLanguageFromLocale(currentLocale);
          let key = location.lat + "," + location.lng + "_" + query.trim() + "_" + language;

          let result = getJSONWithExpiry(key);
          if (result) {
            autocomplete.preloaderHide();
            render(result);
          } else {
            let request = {
              location: location,
              radius: "50000",
              keyword: query,
              language: language
            };

            getPlacesService().nearbySearch(request, function (items) {
              if (items) {
                for (let i = 0; i < items.length; i += 1) {
                  let item = items[i];
                  let location = item.geometry.location;
                  results.push({
                    id: item.place_id,
                    text: item.name + ", " + item.vicinity,
                    location: { lat: location.lat(), lng: location.lng() }
                  });
                }
                setJSONWithExpiry(key, results, MONTH_IN_MILLIS);
              }
              render(results);
              autocomplete.preloaderHide();
            });
          }
        }, 500);
      };

      loadItems(this, query, render);
    },
    renderItem: function (item, index) {
      return (
        "<li>" +
        '<label class="item-radio item-content" data-value="' +
        item.value +
        '">' +
        '<div class="item-inner">' +
        '<div class="item-title">' +
        '<span style="margin-right: 8px; color: red" class="icon icon-location"></span>' +
        item.text +
        "</div>" +
        "</div>" +
        " </label>" +
        "</li>"
      );
    },
    on: {
      change: function (value) {
        if (value.length > 0 && value[0]) {
          callback(value[0]);
        }
      }
    }
  };

  let promise = new Promise(function (resolve, reject) {
    initMapService(function () {
      resolve(f7.autocomplete.create({ ...defaultOptions, ...options }));
    });
  });

  return promise;
}

export function getPlaceDetails(placeId) {
  let language = getLanguageFromLocale(currentLocale);
  let key = placeId + "_" + language;
  let result = getJSONWithExpiry(key);
  if (result) {
    return Promise.resolve(result);
  } else {
    return new Promise(function (resolve, reject) {
      let request = {
        placeId: placeId,
        fields: ["address_component", "formatted_address"],
        language: language
      };

      getPlacesService().getDetails(request, function (result, status) {
        if (status == google.maps.places.PlacesServiceStatus.OK) {
          setJSONWithExpiry(key, result, MONTH_IN_MILLIS);
          resolve(result);
        } else {
          reject();
        }
      });
    });
  }
}

export function getAddressFromPlaceDetails(place) {
  let addressComponents = place.address_components;
  let streetNumber;
  let route;
  let street;
  let subdistrict = "";
  let district = "";
  let province = "";
  let zipcode = "";

  addressComponents.forEach(component => {
    if (component.types.includes("street_number")) {
      streetNumber = component.long_name;
    } else if (component.types.includes("route")) {
      route = component.long_name;
    } else if (component.types.includes("locality")) {
      subdistrict = component.long_name;
    } else if (
      component.types.includes("administrative_area_level_2")
    ) {
      district = component.long_name;
    } else if (
      component.types.includes("administrative_area_level_1")
    ) {
      province = component.long_name;
    } else if (component.types.includes("postal_code")) {
      zipcode = component.long_name;
    }
  });

  if (!route) {
    let addressParts = place.formatted_address.split(",");
    let firstAddressItem = place.formatted_address.split(
      ","
    )[0];

    if (
      addressParts.length > 2 &&
      firstAddressItem !== streetNumber &&
      firstAddressItem !== subdistrict &&
      firstAddressItem !== district &&
      firstAddressItem !== province &&
      firstAddressItem !== zipcode
    ) {
      route = firstAddressItem
        .replace(subdistrict, "")
        .replace(district, "")
        .replace(province, "")
        .replace(zipcode, "")
        .trim();
    }
  }

  if (streetNumber && route) {
    street = streetNumber + " " + route;
  } else if (streetNumber) {
    street = streetNumber;
  } else if (route) {
    street = route;
  } else {
    street = "";
  }

  return { building: "", moo: "", street: street, subdistrict: subdistrict, district: district, province: province, zipcode: zipcode };
}

var mapPolygonsPromise;
function getMapPolygons() {
  if (mapPolygonsPromise == null) {
    mapPolygonsPromise = new Promise(function (resolve, reject) {
      initMapService(function () {
        return fetch("/krabi.geojson")
          .then(res => res.json())
          .then(json => {
            let mapPolygons = [];
            let features = json.features;
            for (var feature of features) {
              if (feature.geometry.type === "Polygon") {
                let polygons = [];
                feature.geometry.coordinates[0].forEach(function (coordinate) {
                  polygons.push({ lat: coordinate[1], lng: coordinate[0] });
                });

                var poly = new google.maps.Polygon({
                  path: polygons
                });
                mapPolygons.push({ poly: poly, deliverable: feature.properties.deliverable });
              }
            }

            resolve(mapPolygons);
          }).catch(function (error) {
            console.log(error);
            reject();
          });
      });
    });
  }

  return mapPolygonsPromise;
}

export async function isDeliverable(location) {
  return getMapPolygons().then(function (polygons) {
    let latLng = new google.maps.LatLng(location.lat, location.lng);
    let result = polygons.find(item => google.maps.geometry.poly.containsLocation(latLng, item.poly));
    return result ? result.deliverable : false;
  });
}